import { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { getConfidants, createReport } from '../api';
import './ReportButton.css';

function ReportButton() {
  const { token } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [confidants, setConfidants] = useState([]);
  const [selectedConfidant, setSelectedConfidant] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingConfidants, setLoadingConfidants] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isOpen && confidants.length === 0) {
      loadConfidants();
    }
  }, [isOpen]);

  const loadConfidants = async () => {
    setLoadingConfidants(true);
    try {
      const response = await getConfidants(token);
      setConfidants(response.data || []);
    } catch (err) {
      setError('Impossible de charger la liste des confidents');
    } finally {
      setLoadingConfidants(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    setError('');
    setSuccess(false);
  };

  const handleClose = () => {
    setIsOpen(false);
    setSelectedConfidant('');
    setMessage('');
    setError('');
    setSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedConfidant) {
      setError('Veuillez choisir un confident');
      return;
    }

    if (!message.trim()) {
      setError('Veuillez entrer un message');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await createReport(token, parseInt(selectedConfidant), message);
      setSuccess(true);
      setSelectedConfidant('');
      setMessage('');
    } catch (err) {
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button className="report-button" onClick={handleOpen} aria-label="Signaler du harcelement">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
        </svg>
        <span>Signaler du harcèlement</span>
      </button>

      {isOpen && (
        <div className="report-overlay" onClick={handleClose}>
          <div className="report-modal" onClick={(e) => e.stopPropagation()}>
            <div className="report-modal-header">
              <h2>Signaler du harcèlement</h2>
              <button className="report-modal-close" onClick={handleClose} aria-label="Fermer">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>

            {success ? (
              <div className="report-success">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                </svg>
                <p>Ton signalement a bien été envoyé.</p>
                <p>Un confident te contactera bientôt.</p>
                <button className="report-success-button" onClick={handleClose}>Fermer</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="report-form">
                <p className="report-intro">
                  Si tu vis une situation de harcèlement, tu peux en parler en toute confiance.
                  Choisis un confident et explique ta situation.
                </p>

                {error && <div className="report-error">{error}</div>}

                <div className="report-field">
                  <label htmlFor="confidant">Choisis un confident</label>
                  {loadingConfidants ? (
                    <div className="report-loading">Chargement...</div>
                  ) : (
                    <select
                      id="confidant"
                      value={selectedConfidant}
                      onChange={(e) => setSelectedConfidant(e.target.value)}
                      disabled={loading}
                    >
                      <option value="">-- Sélectionne un confident --</option>
                      {confidants.map((confidant) => (
                        <option key={confidant.id} value={confidant.id}>
                          {confidant.first_name} {confidant.last_name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="report-field">
                  <label htmlFor="message">Ton message</label>
                  <textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Décris ta situation..."
                    rows={5}
                    maxLength={2000}
                    disabled={loading}
                  />
                  <span className="report-char-count">{message.length}/2000</span>
                </div>

                <button type="submit" className="report-submit" disabled={loading}>
                  {loading ? 'Envoi en cours...' : 'Envoyer le signalement'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default ReportButton;
